import SectionPanel from '@/components/SectionPanel';
import { useOrganization } from "src/auth/AuthContext";
import { Flex, Input, SimpleGrid, Text, Textarea } from "@chakra-ui/react";
import ErrorMessage from "src/components/ErrorMessage";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useState, JSX } from "react";
import { useNavigate } from "react-router-dom";
import { Transfer } from "src/api/Transfer";
import DateSelectController from "@/components/fields/DateSelect";
import {
  Select as ReactSelect
} from "chakra-react-select";
import { useProjectNames } from "@/hooks/useProjectNames";
import { transferService } from "src/service/TransferService";
import CancelButton from "src/components/buttons/CancelButton";
import SubmitButton from "@/components/buttons/SubmitButton";
import { Field } from "@/components/ui/field";
import { AmountInputController } from "@/components/fields/AmountInput";
import { LabelAndValueOption } from "@/data/LabelAndValueOption";
import { ProjectSelectController } from "@/components/fields/ProjectSelect";
import { partnerService } from "@/service/PartnerService";
import { donationsCodeService } from "@/service/DonationCodeService";
import CurrencySelectController from "@/components/fields/CurrencySelect";
import {
  NumberInputField,
  NumberInputRoot,
} from "@/components/ui/number-input";
import { convert, DonationCodeController, DonationCodeOption } from "@/components/fields/DonationCodeSelect";
import BudgetYearController from "@/components/fields/BudgetYearController";
import FundSourceController from "@/components/fields/FundSourceController";
import { Alert } from "@/components/ui/alert";
import { AmountUtil } from "@/data/AmountUtil";
import FormPage from '@/components/FormPage';
import { useI18n } from '@/i18n/I18nContext';

export interface TransfersFormProps {
  transferId?: number;
  organizationId: number;
  onSubmit: (ps: Transfer) => Promise<void>;
  viewOnly?: boolean;
  header: string;
}

const TransfersForm = ({
  transferId,
  organizationId,
  onSubmit,
  viewOnly,
  header,
}: TransfersFormProps): JSX.Element => {
  const { t } = useI18n();
  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isSubmitting },
  } = useForm<Transfer>({
    defaultValues: {
      id: transferId ?? -1,
      transferDate: new Date(),
      partnerId: -1,
      projectId: -1,
      budgetYear: new Date().getFullYear(),
      donationCode: "",
      fundSourceId: null,
      fundSourceName: "",
      total: 0,
      overhead: 0,
      transfer: 0,
      currency: "",
      rate: 1,
      comments: "",
      organizationId: organizationId,
    },
  });

  const [serverError, setServerError] = useState("");
  const { options: projectNames } = useProjectNames();
  const [partnerNames, setPartnerNames] = useState<LabelAndValueOption[]>([]);
  const [donationCodes, setDonationCodes] = useState<DonationCodeOption[]>([]);
  const [institutions, setInstitutions] = useState<string[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (transferId && transferId > -1) {
      transferService.find(transferId).then((p) => reset(p));
    }
  }, [transferId, reset]);

  useEffect(() => {
    const partnersPromise = partnerService.listAll().then(ns => {
      setPartnerNames(ns.map(n => ({ label: n.name, value: n.id })));
    });
    const codesPromise = donationsCodeService
      .listAll()
      .then(r => setDonationCodes(convert(r)));
    const institutionsPromise = transferService.fundSourceNames().then(setInstitutions);

    Promise.all([partnersPromise, codesPromise, institutionsPromise]);
  }, []);

  const organization = useOrganization();
  const homeCurrency = organization?.currency;
  const readOnly = (viewOnly ?? false) || !homeCurrency;
  const watchTotal = watch("total", 0);
  const watchOverhead = watch("overhead", 0);
  const watchTransfer = watch("transfer", 0);
  const watchRate = watch("rate", 1);
  const watchCurrency = watch("currency", "");

  useEffect(() => {
    if (!(transferId > -1) && homeCurrency && watchCurrency === homeCurrency) setValue('rate', 1);
  }, [transferId, homeCurrency, watchCurrency, setValue]);

  const sameCurrencyEntry = !(transferId > -1) && watchCurrency === homeCurrency;

  const overheadAmount = watchTotal * ((watchOverhead ?? 0));
  const suggestedTransfer = watchTotal - overheadAmount;
  const transferDiff = watchTransfer - suggestedTransfer;
  const showTransferDiff = Math.abs(transferDiff) >= 0.01;

  const onSubmitInternal = (t: Transfer): Promise<void> => {
    if (!homeCurrency || !Number.isFinite(t.rate) || t.rate <= 0) return Promise.resolve();
    return onSubmit(t)
      .then(() => navigate("/transfers"))
      .catch(() => setServerError("Fehler"));
  };

  return (
    <FormPage title={header} description={t('forms.transfers.description')} maxW='5xl'
      onSubmit={handleSubmit(onSubmitInternal)} invalid={!!serverError}
      actions={<>
        <CancelButton onClick={() => navigate('/transfers')} />
        {!readOnly && <SubmitButton isSubmitting={isSubmitting} />}
      </>}>
      {serverError && <ErrorMessage message={serverError} />}
      <SectionPanel title={t('forms.sections.assignment')}>
        <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
          <Controller control={control} name='partnerId'
            rules={{ required: "Partner auswählen", validate: (value) => value > -1 || "Partner auswählen" }}
            render={({ field: { value, name, onChange, onBlur, ref }, fieldState: { error, invalid } }) => (
              <Field id="partnerId" label='Partner' required disabled={readOnly} invalid={invalid} errorText={error?.message}>
                <ReactSelect
                  isSearchable
                  required
                  isDisabled={readOnly}
                  autoFocus={true}
                  ref={ref}
                  name={name}
                  inputId='partnerId'
                  onBlur={onBlur}
                  placeholder='Partner'
                  options={partnerNames}
                  onChange={val => onChange(val.value)}
                  value={partnerNames.find(c => c.value === value)}
                />
              </Field>
            )} />

          <ProjectSelectController
            control={control}
            projectNames={projectNames}
            name="projectId"
            label="Projekt"
            readOnly={readOnly}
            required
          />
        </SimpleGrid>

        <FundSourceController
          control={control}
          name='fundSourceName'
          label='Institution (oder neue Institution erstellen)'
          readOnly={readOnly}
          options={institutions}
          required
        />

        <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
          <BudgetYearController control={control} name='budgetYear' label='Fonds Jahr' readOnly={readOnly} required />

          <DonationCodeController
            control={control}
            codes={donationCodes}
            name='donationCode'
            label='Kostenstelle'
            readOnly={readOnly}
            required
          />
        </SimpleGrid>
      </SectionPanel>

      <SectionPanel title={t('forms.sections.amountsAndRate')}>
        <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
          <CurrencySelectController control={control} name='currency' readOnly={readOnly} />

          <Controller control={control} name='rate'
            rules={{ required: "Kurs eingeben", validate: (value) => Number.isFinite(value) && value > 0 && (!sameCurrencyEntry || value === 1) || "Rate muss größer als 0 sein" }}
            render={({ field: { value, name, onChange, onBlur, ref }, fieldState: { error, invalid } }) => (
              <Field id="rate" label={`Kurs (${homeCurrency ?? '—'}/${watchCurrency || '—'})`} required disabled={readOnly} invalid={invalid} errorText={error?.message}>
                <Input
                    textAlign='right'
                  type="number"
                  step="0.01"
                  min="0"
                  ref={ref}
                  name={name}
                  value={value}
                  onChange={(e) => onChange(sameCurrencyEntry ? 1 : parseFloat(e.target.value) || 0)}
                  onBlur={onBlur}
                  disabled={readOnly}
                />
              </Field>
            )}
          />

          <DateSelectController label="Kursdatum" control={control} name="transferDate" clearable={false} required disabled={readOnly} />
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
          <AmountInputController control={control} name="total" label={`Auflösung (${homeCurrency ?? '—'})`} readOnly={readOnly} />

          <Controller control={control} name="overhead"
            render={({ field: { value, name, onChange, onBlur, ref }, fieldState: { error, invalid } }) => (
              <Field id="overhead" label='Overhead %' disabled={readOnly} invalid={invalid} errorText={error?.message}>
                <NumberInputRoot
                  step={0.01}
                  formatOptions={{
                    style: "percent",
                  }}
                  min={0}
                  max={99}
                  value={((value ?? 0) * 100).toString()}
                  onValueChange={(ev) => {
                    const numericValue = Number.isFinite(ev.valueAsNumber) ? ev.valueAsNumber : 0;
                    onChange(numericValue);
                  }}
                  onBlur={onBlur}
                  ref={ref}
                  name={name}
                  disabled={readOnly}
                  width='100%'
                >
                  <NumberInputField textAlign="right" pr="8" />
                </NumberInputRoot>
              </Field>
            )}
          />

          <AmountInputController control={control} name="transfer" label={`Transfer (${homeCurrency ?? '—'})`} readOnly={readOnly} />
        </SimpleGrid>
      </SectionPanel>

      <SectionPanel title={t('forms.transfers.summary')}>
        <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
          <Flex justifyContent="space-between" gap='4' wrap='wrap'>
            <Text color='fg.muted'>Transfer ({homeCurrency ?? '-'})</Text>
            <Text fontWeight="bold">{AmountUtil.format(watchTransfer)}</Text>
          </Flex>
          <Flex justifyContent="space-between" gap='4' wrap='wrap'>
            <Text color='fg.muted'>{`Transfer (${watchCurrency || "-"})`}</Text>
            <Text fontWeight="bold">{AmountUtil.format(watchTransfer * watchRate)}</Text>
          </Flex>
          <Flex justifyContent="space-between" gap='4' wrap='wrap'>
            <Text color='fg.muted'>Overhead Betrag</Text>
            <Text fontWeight="bold">{AmountUtil.format(overheadAmount)}</Text>
          </Flex>
          <Flex justifyContent="space-between" gap='4' wrap='wrap'>
            <Text color='fg.muted'>Erwarteter Transfer (Auflösung - Overhead)</Text>
            <Text fontWeight="bold">{AmountUtil.format(suggestedTransfer)}</Text>
          </Flex>
        </SimpleGrid>
        {showTransferDiff && (
          <Alert mt={3} status='warning' title='Plausibilitätscheck'>
            {`Der Transfer weicht um ${AmountUtil.format(Math.abs(transferDiff))} ${homeCurrency} vom erwarteten Wert ab.`}
          </Alert>
        )}

        <Field id="comments" label="Bemerkungen" disabled={readOnly}>
          <Textarea {...register("comments")} minH="7rem" resize="vertical" />
        </Field>

      </SectionPanel>
    </FormPage>
  );
};

export default TransfersForm;
